import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocketContext } from './SocketContext';

interface Invitation {
  fromUserId: number;
  fromUsername: string;
  gameMode: string;
  wordLength: number;
  useUmlauts: boolean;
}

interface InvitationContextType {
  pendingInvitation: Invitation | null;
  setPendingInvitation: (invitation: Invitation | null) => void;
  acceptInvitation: () => void;
  rejectInvitation: () => void;
}

const InvitationContext = createContext<InvitationContextType | undefined>(undefined);

// Create a separate component that handles navigation (needs to be inside Router)
const InvitationNavigationHandler: React.FC = () => {
  const navigate = useNavigate();
  const { socket } = useSocketContext();
  const { setPendingInvitation } = useInvitationContext();

  useEffect(() => {
    if (!socket) return;

    const handleLobbyCreated = (data: any) => {
      console.log('Lobby created, navigating to:', `/lobby?lobbyId=${data.lobbyId}`);
      // Clear invitation when lobby is created (invitation accepted)
      setPendingInvitation(null);
      // Navigate to lobby - this works from any page
      navigate(`/lobby?lobbyId=${data.lobbyId}`);
    };

    socket.on('lobby-created', handleLobbyCreated);

    return () => {
      socket.off('lobby-created', handleLobbyCreated);
    };
  }, [socket, navigate, setPendingInvitation]);

  return null; // This component doesn't render anything
};

export const InvitationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pendingInvitation, setPendingInvitation] = useState<Invitation | null>(null);
  const { socket } = useSocketContext();

  // Load pending invitation from localStorage on mount
  useEffect(() => {
    const storedInvitation = localStorage.getItem('pendingInvitation');
    if (storedInvitation) {
      try {
        setPendingInvitation(JSON.parse(storedInvitation));
      } catch (err) {
        console.error('Error loading invitation from localStorage:', err);
        localStorage.removeItem('pendingInvitation');
      }
    }
  }, []);

  // Save pending invitation to localStorage whenever it changes
  useEffect(() => {
    if (pendingInvitation) {
      localStorage.setItem('pendingInvitation', JSON.stringify(pendingInvitation));
    } else {
      localStorage.removeItem('pendingInvitation');
    }
  }, [pendingInvitation]);

  // Listen for invitation events
  useEffect(() => {
    if (!socket) return;

    const handleInvitationReceived = (data: Invitation) => {
      console.log('Invitation received in context:', data);
      // Always update the invitation, even if one already exists (new invitation replaces old one)
      setPendingInvitation(data);
    };

    const handleInvitationRejected = () => {
      setPendingInvitation(null);
    };

    socket.on('invitation-received', handleInvitationReceived);
    socket.on('invitation-rejected', handleInvitationRejected);

    return () => {
      socket.off('invitation-received', handleInvitationReceived);
      socket.off('invitation-rejected', handleInvitationRejected);
    };
  }, [socket]);

  const acceptInvitation = () => {
    if (!socket || !pendingInvitation) return;
    socket.emit('accept-invitation', {
      fromUserId: pendingInvitation.fromUserId
    });
    // Don't clear invitation immediately - wait for lobby-created event
  };

  const rejectInvitation = () => {
    if (!socket || !pendingInvitation) return;
    socket.emit('reject-invitation', {
      fromUserId: pendingInvitation.fromUserId
    });
    setPendingInvitation(null);
  };

  return (
    <InvitationContext.Provider
      value={{
        pendingInvitation,
        setPendingInvitation,
        acceptInvitation,
        rejectInvitation
      }}
    >
      {children}
    </InvitationContext.Provider>
  );
};

export const useInvitationContext = () => {
  const context = useContext(InvitationContext);
  if (context === undefined) {
    throw new Error('useInvitationContext must be used within an InvitationProvider');
  }
  return context;
};

// Export navigation handler separately
export { InvitationNavigationHandler };

